<!-- src/components/Common/HighFrictionConfirmModal.vue -->
<template>
  <div v-if="visible" class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60">
    <div class="m-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
      <h2 class="mb-3 text-lg font-bold text-gray-900">{{ title }}</h2>
      <p class="mb-4 whitespace-pre-wrap text-sm text-gray-600">{{ message }}</p>

      <div v-if="requiresReason" class="mb-4">
        <label
          for="friction-reason"
          class="mb-1 block text-left text-sm font-medium text-gray-700"
          >{{ reasonLabel }}</label
        >
        <input
          v-model="reasonInput"
          type="text"
          id="friction-reason"
          class="form-input w-full"
          placeholder="Reason is required"
          :disabled="working"
        />
      </div>

      <div class="mb-4">
        <input
          v-model="userInput"
          type="text"
          class="form-input w-full text-center"
          :placeholder="`Type '${matchString}' to confirm`"
          :disabled="working"
          @keyup.enter="confirm"
        />
      </div>

      <div class="flex justify-end gap-3">
        <button
          class="rounded-md bg-gray-200 px-4 py-2 text-sm text-gray-800 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="working"
          @click="cancel"
        >
          Cancel
        </button>
        <!-- While an async onConfirm runs, the modal stays OPEN with this
             button busy — closing instantly left the operator staring at an
             unchanged screen with a live Delete button (button-guard sweep,
             2026-08-16). One fix here guards every high-friction flow.
             BusyButton's stacked states keep the size constant, so the swap
             to the spinner never wobbles the layout (Erik's design ruling). -->
        <BusyButton
          class="rounded-md bg-red-600 px-4 py-2 text-sm text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
          :busy="working"
          busy-label="Working…"
          :disabled="isConfirmDisabled"
          @click="confirm"
        >
          {{ confirmText }}
        </BusyButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { eventBus } from '@/utils/eventBus'
import BusyButton from '@/components/Common/BusyButton.vue'

const visible = ref(false)
const title = ref('Confirm Action')
const message = ref('')
const matchString = ref('')
const confirmText = ref('Confirm')
const userInput = ref('')
const working = ref(false)
let confirmCallback = null

// New state for the reason input
const requiresReason = ref(false)
const reasonLabel = ref('Reason')
const reasonInput = ref('')

const isConfirmDisabled = computed(() => {
  const textMatchFails = userInput.value !== matchString.value
  const reasonIsMissing = requiresReason.value && !reasonInput.value.trim()
  return textMatchFails || reasonIsMissing
})

eventBus.on('high-friction-confirm', options => {
  title.value = options.title || 'Confirm Action'
  message.value = options.text || 'Are you sure?'
  matchString.value = options.matchString || ''
  confirmText.value = options.confirmButtonText || 'Confirm'
  confirmCallback = options.onConfirm || null
  requiresReason.value = options.requiresReason || false
  reasonLabel.value = options.reasonLabel || 'Reason'
  userInput.value = ''
  reasonInput.value = '' // Reset reason input
  working.value = false
  visible.value = true
})

const confirm = () => {
  if (working.value) return
  if (isConfirmDisabled.value || !confirmCallback) {
    if (!confirmCallback) visible.value = false
    return
  }
  const result = confirmCallback({ reason: reasonInput.value }) // Pass reason in an object
  if (result && typeof result.then === 'function') {
    // Async onConfirm: hold the modal open, busy, until it settles. Errors
    // are the caller's to surface (their own try/catch + toast); the modal's
    // job is only to not lie about being done.
    working.value = true
    result.finally(() => {
      working.value = false
      visible.value = false
      confirmCallback = null
    })
  } else {
    visible.value = false
    confirmCallback = null
  }
}

const cancel = () => {
  if (working.value) return
  visible.value = false
  confirmCallback = null
}
</script>
